'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useEffect, useMemo, useState } from 'react';
import { Button, Container } from '@mantine/core';
import {
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandLinkedin,
  IconPhone,
  IconMail,
  IconMapPin,
  IconArrowRight,
} from '@tabler/icons-react';

import classes from './ClassicFooter.module.scss';
import { useDictionary } from '@/src/dictionaries/dictionary-provider';
import { FooterEditorModal } from './FooterEditorModale';

type FooterLink = {
  label: string;
  link: string;
};

type FooterGroup = {
  title: string;
  links: FooterLink[];
};

export function ClassicFooter() {
  const dictionary: any = useDictionary();
  const [canEditFooter, setCanEditFooter] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await fetch('/api/validate-token', {
          method: 'GET',
          credentials: 'include',
        });

        if (!res.ok) {
          setCanEditFooter(false);
          return;
        }

        const data = await res.json();
        const role = data?.user?.role ?? null;
        const valid = !!data?.valid;

        setCanEditFooter(valid && (role === 'superAdmin' || role === 'admin'));
      } catch (err) {
        console.error('Auth validation failed', err);
        setCanEditFooter(false);
      }
    };

    checkAuth();
  }, []);

  const footer = dictionary?.footer ?? {};
  const footerLinks: FooterGroup[] = Array.isArray(footer.links) ? footer.links : [];

  const groups = useMemo(() => {
    return footerLinks.map((group, index) => (
      <div className={classes.group} key={`${group?.title ?? 'group'}-${index}`}>
        <h3 className={classes.groupTitle}>{group.title}</h3>

        <div className={classes.groupLinks}>
          {(group.links ?? []).map((l, i) => (
            <Link key={`${l.link}-${i}`} className={classes.link} href={l.link || '#'}>
              {l.label}
            </Link>
          ))}
        </div>
      </div>
    ));
  }, [footerLinks]);

  const contactItems = useMemo(() => {
    return [
      dictionary?.address && { label: 'Adresse', value: dictionary.address },
      dictionary?.whatsappPhone && { label: 'Contact', value: dictionary.whatsappPhone },
      dictionary?.metadata?.organization?.email && {
        label: 'Email',
        value: dictionary.metadata.organization.email,
      },
    ].filter(Boolean) as { label: string; value: string }[];
  }, [dictionary]);

  const linkedinUrl = useMemo(() => {
    return (
      footer.linkedin ||
      dictionary?.metadata?.organization?.sameAs?.find((url: string) =>
        url?.includes('linkedin')
      )
    );
  }, [dictionary, footer.linkedin]);

  const getIcon = (label: string) => {
    const s = label.toLowerCase();

    if (s.includes('contact') || s.includes('phone')) return <IconPhone size={18} />;
    if (s.includes('email')) return <IconMail size={18} />;

    if (s.includes('adresse') || s.includes('address') || s.includes('studio')) {
      return <IconMapPin size={18} />;
    }

    return null;
  };

  return (
    <footer className={classes.footer} role="contentinfo">
      {canEditFooter && <FooterEditorModal />}

      <Container size="xl" className={classes.container}>
        <div className={classes.grid}>
          <div className={classes.brand}>
            <Link href="/" className={classes.brandLink} aria-label="Retour à l'accueil">
              <span className={classes.logoWrap}>
                {dictionary?.logoUrl ? (
                  <Image
                    src={dictionary.logoUrl}
                    alt={`${dictionary?.compagnyName ?? 'MiniMonkeys'} logo`}
                    width={220}
                    height={90}
                    sizes="(max-width: 620px) 170px, 220px"
                    className={classes.logo}
                    priority={false}
                  />
                ) : (
                  <span className={classes.logoFallback} aria-hidden="true" />
                )}
              </span>
            </Link>

            <p className={classes.description}>{footer.description}</p>

            <div className={classes.social}>
              {footer.facebook && (
                <Link
                  href={footer.facebook}
                  className={classes.socialLink}
                  aria-label="Facebook"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <IconBrandFacebook size={18} />
                </Link>
              )}

              {footer.instagram && (
                <Link
                  href={footer.instagram}
                  className={classes.socialLink}
                  aria-label="Instagram"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <IconBrandInstagram size={18} />
                </Link>
              )}

              {linkedinUrl && (
                <Link
                  href={linkedinUrl}
                  className={classes.socialLink}
                  aria-label="LinkedIn"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <IconBrandLinkedin size={18} />
                </Link>
              )}
            </div>
          </div>

          <div className={classes.links}>
            <div className={classes.linksHeader}>
              <h3 className={classes.sectionTitle}>{footer.linksTitle ?? 'Navigation'}</h3>
            </div>

            <div className={classes.linksGrid}>{groups}</div>
          </div>

          <div className={classes.contact}>
            <div className={classes.contactHeader}>
              <h3 className={classes.sectionTitle}>Contact</h3>
            </div>

            <div className={classes.contactList}>
              {contactItems.map((item) => (
                <div key={item.label} className={classes.contactItem}>
                  <span className={classes.contactIcon}>{getIcon(item.label)}</span>

                  <div className={classes.contactText}>
                    <strong>{item.label}</strong>
                    <span>{item.value}</span>
                  </div>
                </div>
              ))}
            </div>

            <Button
              component={Link}
              href={dictionary?.pages?.contact?.link ?? '/contact'}
              className={classes.ctaButton}
              rightSection={<IconArrowRight size={18} />}
            >
              {dictionary?.pages?.contact?.label ?? 'Contact'}
            </Button>
          </div>
        </div>

        <div className={classes.bottom}>
          <div className={classes.bottomLeft}>
            <Link href="https://hestia-innovation.com" className={classes.bottomLink}>
              UI/UX crafted in Paris
            </Link>

            <span className={classes.sep} aria-hidden="true" />

            <Link href="/sitemap.xml" className={classes.bottomLink}>
              {footer.sitemap ?? 'Sitemap'}
            </Link>
          </div>

          <p className={classes.copy}>
            © {new Date().getFullYear()} {dictionary?.compagnyName ?? 'Hestia Innovation'} —{' '}
            {footer.copyright}
          </p>
        </div>
      </Container>
    </footer>
  );
}